import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
} from '@angular/core';
import {
  Order,
  EligibilityAssessment,
  OrderDocument,
  OrderStatus,
  OrderPaymentCard,
  ShipmentStatus,
  OrderDocumentType,
} from '../../../../../models/order.model';
import { TagStatusComponent } from '../../../../../../shared/components/tag-status/tag-status.component';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { Customer } from '../../../../../models/customer.model';
import { PhoneFormatPipe } from '../../../../../../shared/pipes/format-phone.pipe';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DialogModule } from 'primeng/dialog';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { OrderService } from '../../../../../services/order.service';
import { SelectOption } from '../../../../../../shared/models/dropdowm.model';
import { FormsModule } from '@angular/forms';
import { OrderAssessmentComponent } from '../order-assessment/order-assessment.component';
import { PaginatorModule } from 'primeng/paginator';
import { OrderInstallmentsComponent } from './components/order-installments/order-installments.component';
import { OrderDownPaymentComponent } from './components/order-down-payment/order-down-payment.component';
import { RouterLink } from '@angular/router';
import { TableModule } from 'primeng/table';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { OrderCancelDialogComponent } from '../order-cancel-dialog/order-cancel-dialog.component';
import { canCancelOrder } from '../../utils/order-details.util';
import { OrderCancellationCardComponent } from '../order-cancellation-card/order-cancellation-card.component';

@Component({
  selector: 'app-order-details-tab-view',
  imports: [
    FormsModule,
    TagStatusComponent,
    ButtonModule,
    TabsModule,
    DialogModule,
    TitleCasePipe,
    SelectModule,
    DatePipe,
    OrderAssessmentComponent,
    PaginatorModule,
    OrderInstallmentsComponent,
    OrderDownPaymentComponent,
    RouterLink,
    TableModule,
    ConfirmDialogModule,
    OrderCancelDialogComponent,
    OrderCancellationCardComponent,
  ],
  templateUrl: './order-details-tab-view.component.html',
  styleUrl: './order-details-tab-view.component.scss',
  providers: [ConfirmationService],
})
export class OrderDetailsTabViewComponent {
  order = input<Order | null>(null);
  eligibilityAssessment = input<EligibilityAssessment | null>(null);
  orderNumber = input<string>('');
  statusText = input<string>('');
  @Output() setLoading = new EventEmitter<boolean>();
  @Output() reloadOrder = new EventEmitter();

  private phoneFormatPipe = inject(PhoneFormatPipe);
  private sanitizer = inject(DomSanitizer);
  private orderService = inject(OrderService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  contractDialogVisible = false;
  cancelDialogVisible = signal(false);

  statusTag = computed(() => {
    const status = this.order()?.status;
    return status ? this.orderService.getStatusTag(status) : '';
  });

  canCancel = computed(() => canCancelOrder(this.order()));

  shipmentStatus = ShipmentStatus.AwaitingPickup;

  protected readonly shipmentStatusOptions: SelectOption[] =
    this.orderService.getShipmentStatusDropdownOptions();

  isOrderCancelled = computed(
    () => this.order()?.status === OrderStatus.Cancelled,
  );

  get product(): any {
    return this.order()?.product;
  }

  get customer(): Customer {
    return this.order()?.customer as Customer;
  }

  get documents(): any[] {
    return (this.order()?.documents ??
      this.order()?.documents ??
      []) as OrderDocument[];
  }

  get payment(): any {
    return null;
  }

  get primaryEmail(): string {
    const c = this.customer;
    if (!c) return '-';

    const list = c.electronicAddresses ?? [];
    return list?.[0]?.address ?? '-';
  }

  get primaryPhone(): string {
    const c = this.customer;
    if (!c) return '-';

    const list = c.phones ?? [];
    if (list.length <= 0) return '-';

    const phone = list[0];
    const countryCode = phone!.countryCode;
    const phoneFormatted = this.phoneFormatPipe.transform(
      phone!.number,
      countryCode,
      { includeCountryCode: true },
    );
    return phoneFormatted;
  }

  get hasContract() {
    return this.order()?.contract && this.order()?.contract?.contractUrl;
  }

  productColor = computed(() => {
    if (!this.order()) return '-';

    const corAttr = this.order()!.product.attributes.filter(
      (x) => x.variationAttributeName.toLocaleLowerCase() === 'cor',
    );
    return corAttr.length > 0 ? corAttr[0]!.variationAttributeValue : '-';
  });

  productCapacity = computed(() => {
    if (!this.order()) return '-';

    const corAttr = this.order()!.product.attributes.filter(
      (x) => x.variationAttributeName.toLocaleLowerCase() === 'capacidade',
    );
    return corAttr.length > 0 ? corAttr[0]!.variationAttributeValue : '-';
  });

  contractTag = computed(() =>
    this.orderService.getContractStatusTag(this.order()?.contract),
  );

  contractSigned = computed(() =>
    this.orderService.isContractSigned(this.order()?.contract),
  );

  currentCard = computed(() => {
    if (!this.order() || !this.order()?.paymentCards) return null;

    const defaultCard = this.order()!.paymentCards!.filter((x) => x.isDefault);
    return defaultCard[0];
  });

  shipment = computed(() => {
    if (this.order()?.shipments.length === 0) return null;

    return this.order()?.shipments[0];
  });

  orderDelivered = computed(() => {
    if (!this.order()) return false;

    return this.order()!.status === OrderStatus.Delivered;
  });

  private readonly shipmentFlowOrder: ShipmentStatus[] = [
    ShipmentStatus.AwaitingPickup,
    ShipmentStatus.PickedUp,
    ShipmentStatus.InTransit,
    ShipmentStatus.OutForDelivery,
    ShipmentStatus.Delivered,
    ShipmentStatus.DeliveryException,
    ShipmentStatus.ReturnedToSender,
  ];

  ngOnInit(): void {
    if (this.shipment()) this.shipmentStatus = this.shipment()!.status;
  }

  openContractDialog(): void {
    this.contractDialogVisible = true;
  }

  getContractUrl(): SafeResourceUrl | null {
    if (!this.order()?.contract) return null;

    const encodedUrl = encodeURIComponent(this.order()!.contract!.contractUrl);
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`,
    );
  }

  formatCardExpirationDate(card: OrderPaymentCard): string {
    return (
      String(card.expirationMonth).padStart(2, '0') + '/' + card.expirationYear
    );
  }

  getDocumentNameByType(type: OrderDocumentType) {
    switch (type) {
      case OrderDocumentType.ProofOfAddress:
        return 'Comprovante de Residência';
      case OrderDocumentType.BankStatement:
        return 'Comprovante Bancário';
      case OrderDocumentType.FaceCapture:
        return 'Captura da Face';
      case OrderDocumentType.Passport:
        return 'Passaporte';
      default:
        return 'Documento';
    }
  }

  getOrderStatusDescription(status: OrderStatus): string {
    return this.orderService.getStatusDescription(status);
  }

  getShipmentStatusDescription(status: ShipmentStatus): string {
    return this.orderService.getShipmentStatusDescription(status);
  }

  updateShipmentStatus(): void {
    const current = this.getCurrentShipmentStatus();
    const next = this.shipmentStatus;

    if (!current) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não foi possível identificar o status atual do transporte.',
        life: 3000,
      });
      return;
    }

    if (current === next) {
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso!',
        detail: 'O status dos dados de envio foram atualizados sucesso!',
        life: 2000,
      });
      return;
    }

    if (!this.canMoveShipmentStatusForward(current, next)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Status inválido',
        detail:
          'Você só pode avançar o status do transporte. Não é permitido voltar para um status anterior.',
        life: 4000,
      });
      return;
    }

    const currentLabel = this.getShipmentStatusDescription(current);
    const nextLabel = this.getShipmentStatusDescription(next);

    this.confirmationService.confirm({
      header: 'Confirmar atualização do status de transporte',
      message:
        `Você está prestes a alterar o status do transporte de ` +
        `"${currentLabel}" para "${nextLabel}".<br/><br/>` +
        `Atenção! O status só pode avançar, isso significa, que é não permitido voltar para status anteriores após a confirmação.<br/><br/>` +
        `Deseja confirmar?`,
      acceptLabel: 'Confirmar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'btn-primary p-button-rounded',
      rejectButtonStyleClass:
        'p-button-secondary btn-secondary-outline p-button-rounded',
      accept: () => this.persistShipmentStatus(),
    });
  }

  private getCurrentShipmentStatus(): ShipmentStatus | null {
    const status = this.shipment();
    if (!status) return null;

    return status.status;
  }

  private canMoveShipmentStatusForward(
    current: ShipmentStatus,
    next: ShipmentStatus,
  ): boolean {
    const curIdx = this.shipmentFlowOrder.indexOf(current);
    const nextIdx = this.shipmentFlowOrder.indexOf(next);

    // se algo não estiver no flow, bloqueia por segurança
    if (curIdx < 0 || nextIdx < 0) return false;

    // só permite manter ou avançar
    return nextIdx >= curIdx;
  }

  private persistShipmentStatus(): void {
    this.setLoading.emit(true);

    this.orderService
      .updateStatusOrderShipment(
        this.order()!.id,
        this.shipment()!.id,
        this.shipmentStatus,
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso!',
            detail: 'O status dos dados de envio foram atualizados sucesso!',
            life: 2000,
          });

          this.setLoading.emit(false);
          this.reloadOrder.emit();
        },
        error: (e) => {
          this.setLoading.emit(false);

          if (e instanceof HttpErrorResponse) {
            if ([400, 422].includes(e.status)) {
              if (
                e.error.message &&
                (!e.error.errors || e.error.errors.length == 0)
              ) {
                this.messageService.add({
                  severity: 'warn',
                  summary: 'Warning',
                  detail: e.error.message,
                  life: 3000,
                });
              }
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Fail',
                detail:
                  'Ocorreu um erro ao atualizar o status os dados de envio',
                life: 3000,
              });
            }
          }
        },
      });
  }
}
